import React from 'react';

import {
	type ActionElement,
	type CarouselElement,
	type ListElement,
	type MarkdownElement,
	type ParsedElement,
	type SmartLinkElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import { DynamicLayoutCarousel } from './dynamic-layout-carousel';
import { DynamicLayoutList } from './dynamic-layout-list';
import { DynamicLayoutMarkdown } from './dynamic-layout-markdown';
import { DynamicLayoutStandaloneSmartLink } from './dynamic-layout-smart-link';
import { DynamicLayoutProjectHighlights } from './sub-agents/dynamic-layout-project-highlights';
import { DynamicLayoutRelatedPeople } from './sub-agents/dynamic-layout-related-people';

export type ComponentPosition =
	| 'two-rows-bottom'
	| 'two-rows-top'
	| 'two-columns-left'
	| 'two-columns-right'
	| 'single-column';

interface AISearchComponentProps {
	component: ParsedElement;
	position: ComponentPosition;
	isFoldable?: boolean;
}

export const DynamicLayoutComponent = ({
	component,
	position,
	isFoldable = false,
}: AISearchComponentProps): React.JSX.Element | null => {
	if (!component || (typeof component !== 'string' && !component.tag)) {
		return null;
	}

	if (typeof component !== 'string') {
		switch (component.tag) {
			case 'md':
				return <DynamicLayoutMarkdown component={component as MarkdownElement} />;
			case 'list':
				return <DynamicLayoutList component={component as ListElement} />;
			case 'smart-link':
				return <DynamicLayoutStandaloneSmartLink component={component as SmartLinkElement} />;
			case 'carousel':
				return (
					<DynamicLayoutCarousel
						component={component as CarouselElement}
						position={position}
						isFoldable={isFoldable}
					/>
				);
			case 'action-card': {
				const actionCard = component as ActionElement;
				// Validate component has a url and convert to smart-link
				if (actionCard.attributes?.url) {
					const smartLinkComponent: SmartLinkElement = {
						tag: 'smart-link',
						attributes: {
							url: actionCard.attributes.url,
							title: actionCard.attributes?.title,
							type: 'action',
							pos: actionCard.attributes?.pos,
						},
					};
					return <DynamicLayoutStandaloneSmartLink component={smartLinkComponent} />;
				}
				return null;
			}
			case 'related-people':
				return <DynamicLayoutRelatedPeople />;
			case 'project-highlights':
				return <DynamicLayoutProjectHighlights />;
			default:
				return null;
		}
	}

	return null;
};
